import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { GET_ONE, useListContext, useLocale, useQuery, useRecordContext, useTranslate } from 'react-admin';
import { LinearProgress, styled } from '@material-ui/core';
import moment from 'moment-timezone';

import { dedupe } from '@hisports/parsers';

import { SquareAlert as Alert } from '../../SquareAlert';

import { usePermissions } from '../../../http';
import { CALENDAR_VIEWS, useCalendarView, useCompact, useShowCondensedView, useSurfaceSizes } from '../../../resources/events/EventViewSettings';

import { useAssociationSchedulingContext } from '../schedulers/association/AssociationSchedulingContext';
import { SNAP_MINUTES, getEventId, getEventResource } from '../DragAndDropGrid';
import { useSchedulingContext } from './SchedulingContext';
import { buildAssociationCalendarColumns } from './columns/association';
import { buildSeasonCalendarColumns } from './columns/season';
import { buildWeekCalendarColumns } from './columns/week';
import { buildMonthCalendarColumns } from './columns/month';
import { buildDayCalendarColumns } from './columns/day';

export const DATE_FILTER_TYPES = {
  MONTH: 'Month',
  SEASON: 'Season',
}

const CalendarContext = React.createContext(null);

const Loader = styled(LinearProgress)({
  position: 'absolute',
  inset: '0 0 auto 0'
})

const getTimezones = (office, surfaces = [], record) => {
  const timezones = [
    record?.timezone,
    office?.timezone,
    ...surfaces.filter(s => !['TBA', 'NDA'].includes(s?.alias)).map(s => s.timezone),
    moment.tz.guess(),
  ].filter(Boolean);

  return dedupe(timezones);
}

const hasOfficeAccess = (permissions, officeId) => permissions.some(p => p.roleType === 'System' || p.officeIds?.includes(officeId));

const useOffice = (officeId, hasAccess = true) => useQuery({
  type: GET_ONE,
  resource: 'offices',
  payload: {
    id: officeId,
  }
}, {
  enabled: officeId != null && hasAccess,
})

const getCalendarAlert = ({
  view,
  listLoading,
  loading,
  translate,
  selectedGame,
  availability,
  values,
}) => {
  if (view === CALENDAR_VIEWS.ASSOCIATION_CALENDAR) {
    return listLoading ? <Loader color="primary" variant="indeterminate" /> : null;
  }

  if ([CALENDAR_VIEWS.SEASON, CALENDAR_VIEWS.SEASON_CLICK].includes(view)) {
    if (loading) return <Loader color="primary" variant="indeterminate" />;
    if (!values?.length) {
      return (
        <Alert severity="warning">
          {translate('components.calendar.messages.no_schedule_teams')}
        </Alert>
      );
    }
    return null;
  }

  if (!selectedGame) {
    return (
      <>
        <Alert severity="info">
          {translate('components.calendar.messages.select_game')}
        </Alert>
        {loading && <Loader color="primary" variant="indeterminate" />}
      </>
    );
  }

  if (!selectedGame.date) {
    return (
      <>
        <Alert severity="warning">
          {translate('components.calendar.messages.schedule_game')}
        </Alert>
        {loading && <Loader color="primary" variant="indeterminate" />}
      </>
    );
  }

  if (loading) {
    return <Loader color="primary" variant="indeterminate" />;
  }

  if (!availability?.surfaces?.length && view !== CALENDAR_VIEWS.MONTH) {
    return (
      <Alert severity="error">
        {translate('components.calendar.messages.no_home_surface')}
      </Alert>
    );
  }

  if (!values?.length) {
    return (
      <Alert severity="warning">
        {translate('components.calendar.messages.no_result')}
      </Alert>
    );
  }

  return null;
};

export const useCalendarContext = () => useContext(CalendarContext) || {};

export const CalendarProvider = memo(({
  includeVenue,
  popoverActions,
  hideArena,
  disableAuth = false,
  maxColumns: defaultMaxColumns = 5,
  initialStart,
  strictView,
  width,
  date,
  ...props
}) => {
  const { filterValues, loading: listLoading } = useListContext();
  const [ view, setView ] = useCalendarView();
  const [ surfaceSizes ] = useSurfaceSizes()
  const { selectedGame, availability, gameLength, loading, schedule, scheduleTeams, dateFilter, setDateFilter } = useSchedulingContext() || {};
  const [ compact ] = useCompact();

  const {
    surfaces: associationSurfaces = [],
    office: association,
    colOffset: associationColOffset,
    setColOffset: setAssociationColOffset,
  } = useAssociationSchedulingContext() || {};

  const locale = useLocale();
  const translate = useTranslate()
  const record = useRecordContext()
  const permissions = usePermissions();

  const officeId = schedule?.officeId || record?.officeId;
  const { data: scheduleOffice } = useOffice(officeId, hasOfficeAccess(permissions, officeId));
  const [ showCondensedView ] = useShowCondensedView();

  const [ colOffset, setColOffset ] = useState(0);
  const [ navigateToDate, setNavigateToDate ] = useState(null);

  const maxColumns = useMemo(() => {
    if ([CALENDAR_VIEWS.SEASON, CALENDAR_VIEWS.SEASON_CLICK].includes(view)) {
      const maxCol = compact ? 16 : 7;
      const minCol = compact ? 10 : 4;
      const colWidth = compact ? 100 : 200;
      return width ? Math.min(Math.max(Math.floor(width / colWidth), minCol), maxCol) : minCol;
    }

    return defaultMaxColumns;
  }, [compact, defaultMaxColumns, view, width])

  const filteredSurfaces = useMemo(() => (availability?.surfaces || []).filter(surface => {
    if (!surfaceSizes?.length) return true;
    return surfaceSizes.includes(surface.size)
  }), [availability, surfaceSizes])

  const timezones = useMemo(() => {
    if (view === CALENDAR_VIEWS.MONTH) return [];

    if (view === CALENDAR_VIEWS.ASSOCIATION_CALENDAR && association?.timezone) {
      return [association.timezone];
    }

    return getTimezones(scheduleOffice, filteredSurfaces, record);
  }, [association?.timezone, filteredSurfaces, record, scheduleOffice, view])

  const timezone = useMemo(() => {
    if (view !== CALENDAR_VIEWS.MONTH && timezones?.length) {
      return timezones[0];
    } else {
      return moment.tz.guess()
    }
  }, [ timezones, view ])

  useEffect(() => {
    // on mount
    if (strictView) {
      return setView(strictView)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedGame || strictView || [CALENDAR_VIEWS.SEASON, CALENDAR_VIEWS.SEASON_CLICK].includes(view)) return;
    if (!selectedGame.date) {
      setView(CALENDAR_VIEWS.MONTH)
    } else {
      setView(CALENDAR_VIEWS.DAY)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strictView, selectedGame, setView])

  useEffect(() => {
    if (!selectedGame) return;
    if (filteredSurfaces.length && selectedGame?.arenaId) {
      const index = filteredSurfaces?.findIndex(surface => surface.id === selectedGame.arenaId)
      if (index < 0) return
      const offset = index + 1 - maxColumns < 0 ? 0 : index + 1 - maxColumns
      setColOffset(offset)
    } else {
      setColOffset(0);
    }
  }, [ filteredSurfaces, maxColumns, selectedGame ])

  const columns = useMemo(() => {
    const getAlert = (values) => getCalendarAlert({
      view,
      listLoading,
      loading,
      translate,
      selectedGame,
      availability,
      values,
    });

    if (view === CALENDAR_VIEWS.ASSOCIATION_CALENDAR) {
      return buildAssociationCalendarColumns({
        date,
        timezone,
        maxColumns,
        associationSurfaces,
        associationColOffset,
        translate,
        getAlert,
        setAssociationColOffset,
        gameLength,
        filterValues,
      });
    }

    if ([CALENDAR_VIEWS.SEASON, CALENDAR_VIEWS.SEASON_CLICK].includes(view)) {
      return buildSeasonCalendarColumns({
        dateFilter,
        locale,
        timezone,
        availability,
        schedule,
        scheduleTeams,
        showCondensedView,
        selectedGame,
        colOffset,
        maxColumns,
        getAlert,
        setColOffset,
      });
    }

    if (view === CALENDAR_VIEWS.MONTH) {
      return buildMonthCalendarColumns({
        filterValues,
        timezone,
        selectedGame,
        getAlert,
      });
    }

    if (view === CALENDAR_VIEWS.DAY) {
      return buildDayCalendarColumns({
        selectedGame,
        filteredSurfaces,
        availability,
        colOffset,
        maxColumns,
        initialStart,
        timezone,
        translate,
        getAlert,
        setColOffset,
        gameLength,
      });
    }

    if (view === CALENDAR_VIEWS.WEEK) {
      return buildWeekCalendarColumns({
        filterValues,
        timezone,
        getEventResource,
        getEventId,
        SNAP_MINUTES,
      });
    }
  }, [view, selectedGame, translate, loading, availability, listLoading, date, timezone, maxColumns, associationSurfaces, associationColOffset, setAssociationColOffset, gameLength, dateFilter, locale, schedule, scheduleTeams, showCondensedView, colOffset, filterValues, filteredSurfaces, initialStart])

  useEffect(() => {
    // handle dateFilter for month view
    if (view !== CALENDAR_VIEWS.MONTH || !columns?.dates?.length || !timezone) return;

    const startDate = moment.tz(columns.dates[0], timezone).startOf('day').toISOString();
    const endDate = moment.tz(columns.dates.pop(), timezone).endOf('day').toISOString();

    if (startDate != dateFilter?.startDate || endDate != dateFilter?.endDate) {
      setDateFilter({
        startDate,
        endDate,
        type: DATE_FILTER_TYPES.MONTH,
      });
    }
  }, [columns?.dates, view, dateFilter?.endDate, dateFilter?.startDate, setDateFilter, timezone])

  useEffect(() => {
    // init dateFilter for season view
    if (![CALENDAR_VIEWS.SEASON, CALENDAR_VIEWS.SEASON_CLICK].includes(view) || dateFilter?.type === DATE_FILTER_TYPES.SEASON || !schedule?.startDate) return;

    const scheduleStartDate = moment.utc(schedule.startDate);
    const scheduleEndDate = moment.utc(schedule.endDate);
    const oneMonthFromStartDate = scheduleStartDate.clone().add(1, 'month');

    return setDateFilter({
      startDate: scheduleStartDate.format('YYYY-MM-DD'),
      endDate: oneMonthFromStartDate.isAfter(scheduleEndDate, 'day') ? scheduleEndDate?.format('YYYY-MM-DD') : oneMonthFromStartDate.format('YYYY-MM-DD'),
      type: DATE_FILTER_TYPES.SEASON,
    });
  }, [view, dateFilter?.type, schedule?.endDate, schedule?.startDate, setDateFilter])

  const value = useMemo(() => ({
    colOffset,
    maxColumns,
    includeVenue,
    popoverActions,
    hideArena,
    disableAuth,
    initialStart,
    timezone,
    timezones,
    navigateToDate,
    setNavigateToDate,
    ...columns,
  }), [ columns, colOffset, maxColumns, includeVenue, popoverActions, hideArena, disableAuth, initialStart, timezone, timezones, navigateToDate ]);

  return <CalendarContext.Provider value={value} {...props} />
})
